import { useEffect, useState } from "react";

const PHONE = "35799748392";

/** Verified fallback deep link (used before source detection runs). */
export const WHATSAPP_URL =
  "https://wa.me/35799748392?text=Hi!%20I%27m%20a%20lead%20from%20Facebook%20%F0%9F%91%8B";

const MESSAGES: Record<string, string> = {
  facebook: "Hi! I'm a lead from Facebook 👋",
  instagram: "Hi! I'm a lead from Instagram 👋",
  google: "Hi! I'm a lead from Google 👋",
  default: "Hi! I'd like to know more 👋",
};

export type UtmData = {
  utm_source: string;
  gclid: string;
  fbclid: string;
  savedAt?: number;
};

function getParam(name: string, search: string): string {
  return new URLSearchParams(search).get(name) || "";
}

export function captureUtm(): void {
  if (typeof window === "undefined") return;
  const utm_source = getParam("utm_source", window.location.search);
  const gclid = getParam("gclid", window.location.search);
  const fbclid = getParam("fbclid", window.location.search);
  if (utm_source || gclid || fbclid) {
    let prev: Partial<UtmData> = {};
    try {
      prev = JSON.parse(localStorage.getItem("lead_utm") || "{}");
    } catch {
      /* ignore */
    }
    const data: UtmData = {
      utm_source: utm_source || prev.utm_source || "",
      gclid: gclid || prev.gclid || "",
      fbclid: fbclid || prev.fbclid || "",
      savedAt: fbclid ? Date.now() : prev.savedAt || Date.now(),
    };
    try {
      localStorage.setItem("lead_utm", JSON.stringify(data));
    } catch {
      /* ignore */
    }
  }
}

export function readUtm(): UtmData {
  if (typeof window === "undefined") {
    return { utm_source: "", gclid: "", fbclid: "" };
  }
  const fromUrl: UtmData = {
    utm_source: getParam("utm_source", window.location.search),
    gclid: getParam("gclid", window.location.search),
    fbclid: getParam("fbclid", window.location.search),
  };
  let saved: UtmData | null = null;
  try {
    const raw = localStorage.getItem("lead_utm");
    if (raw) {
      const s: UtmData = JSON.parse(raw);
      const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
      if (s.savedAt && Date.now() - s.savedAt < THIRTY_DAYS) saved = s;
    }
  } catch {
    /* ignore */
  }
  // Merge field by field: URL wins, saved values fill gaps (fbclid must not be dropped).
  return {
    utm_source: fromUrl.utm_source || saved?.utm_source || "",
    gclid: fromUrl.gclid || saved?.gclid || "",
    fbclid: fromUrl.fbclid || saved?.fbclid || "",
    savedAt: saved?.savedAt,
  };
}

function detectSource(): keyof typeof MESSAGES {
  const utm = readUtm();
  const src = (utm.utm_source || "").toLowerCase();
  if (src.includes("facebook") || src === "fb") return "facebook";
  if (src.includes("instagram") || src === "ig") return "instagram";
  if (src.includes("google") || src === "adwords" || src === "googleads") return "google";
  if (utm.fbclid) return "facebook";
  if (utm.gclid) return "google";
  return "default";
}

function buildWaLink(): { source: string; url: string } {
  const source = detectSource();
  const text = MESSAGES[source] || MESSAGES.default;
  const url = `https://wa.me/${PHONE}?text=${encodeURIComponent(text)}`;
  return { source, url };
}

/** Public helper: WhatsApp deep link with a source-aware prefilled message. */
export function buildWhatsAppUrl(): string {
  if (typeof window === "undefined") return WHATSAPP_URL;
  return buildWaLink().url;
}

/** Telegram bot link with a site source tag. */
export const TELEGRAM_BOT_URL = "https://t.me/findrates_office_bot?start=utm_source_site";


type WhatsAppButtonProps = {
  className?: string;
  label?: string;
};

export function WhatsAppButton({ className, label = "Message us on WhatsApp" }: WhatsAppButtonProps) {
  const [href, setHref] = useState(WHATSAPP_URL);

  useEffect(() => {
    const { url } = buildWaLink();
    setHref(url);
  }, []);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={
        className ??
        "h-11 inline-flex items-center px-5 rounded-full bg-[#25D366] text-white text-sm font-medium hover:bg-[#1eb457] transition-colors no-underline"
      }
    >
      <span>{label}</span>
    </a>
  );
}
